/* Merchant-managed cart offers. Shopify's discount allocations remain the price authority. */
(() => {
  const propertyName = '_luff_offer';
  const attributeName = '_luff_offer_earned';
  const root = window.Shopify?.routes?.root || '/';
  let running = null;
  let pending = false;

  async function cart() {
    const response = await fetch(`${root}cart.js`, { cache: 'no-store' });
    if (!response.ok) throw new Error('Could not read the cart');
    return response.json();
  }

  async function post(path, body) {
    const response = await fetch(`${root}cart/${path}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || result.message || 'Cart update failed');
    return result;
  }

  async function rules() {
    const response = await fetch(`${root}cart?section_id=cart-offer-data`, { cache: 'no-store' });
    if (!response.ok) throw new Error('Could not read cart offer rules');
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const data = html.getElementById('luff-cart-offer-data');
    if (!data) throw new Error('Cart offer rule section is missing');
    return JSON.parse(data.textContent);
  }

  function earnedByReward(items, ruleRows) {
    const byKey = new Map(items.map(item => [item.key, item]));
    const earned = {};
    for (const row of ruleRows) {
      const item = byKey.get(row.key);
      if (!item) continue;
      for (const offer of row.offers) {
        const id = String(offer.rewardVariantId).split('/').pop();
        const units = Number(offer.units);
        if (!id || !Number.isSafeInteger(units) || units < 1) continue;
        earned[id] = (earned[id] || 0) + item.quantity * units;
      }
    }
    return earned;
  }

  function previousEarned(current) {
    try {
      const parsed = JSON.parse(current.attributes?.[attributeName] || '');
      if (parsed.version === 1 && parsed.token === current.token && parsed.earned) return parsed.earned;
    } catch (_) { /* A cart without an offer ledger is a new offer cart. */ }
    return null;
  }

  async function changeFresh(key, quantity) {
    const current = await cart();
    const item = current.items.find(line => line.key === key);
    if (!item) return false;
    await post('change', { id: item.key, quantity });
    return true;
  }

  async function reconcileOnce() {
    let current = await cart();
    const ruleRows = current.items.length ? await rules() : [];
    const earned = earnedByReward(current.items, ruleRows);
    const previous = previousEarned(current);
    const variants = new Set([...Object.keys(earned), ...Object.keys(previous || {})]);
    let changed = false;

    for (const variantId of variants) {
      const allowance = earned[variantId] || 0;
      const oldAllowance = Number(previous?.[variantId] || 0);
      const matching = current.items.filter(item => String(item.variant_id) === variantId);
      const existing = matching.reduce((sum, item) => sum + item.quantity, 0);
      const missing = Math.max(0, allowance - existing);
      const increase = previous ? Math.min(Math.max(0, allowance - oldAllowance), missing) : missing;
      const decrease = Math.max(0, oldAllowance - allowance);

      if (increase > 0) {
        await post('add', { id: Number(variantId), quantity: increase, properties: { [propertyName]: variantId } });
        changed = true;
        current = await cart();
      }

      // A customer may already have reduced their gifts. Only trim automatic
      // units above the new allowance, preserving smaller choices and paid extras.
      const automatic = matching.filter(item => item.properties?.[propertyName] === variantId)
        .reduce((sum, item) => sum + item.quantity, 0);
      let toRemove = decrease > 0 ? Math.max(0, automatic - allowance) : 0;
      while (toRemove > 0) {
        current = await cart();
        const offered = current.items.find(item => String(item.variant_id) === variantId && item.properties?.[propertyName] === variantId);
        if (!offered) break;
        const removed = Math.min(toRemove, offered.quantity);
        await changeFresh(offered.key, offered.quantity - removed);
        toRemove -= removed;
        changed = true;
      }
    }

    if (variants.size && (!previous || JSON.stringify(previous) !== JSON.stringify(earned))) {
      current = await post('update', { attributes: { [attributeName]: JSON.stringify({ version: 1, token: current.token, earned }) } });
    }

    if (changed) {
      current = await cart();
      if (window.VanillaCartState?.updateCart) window.VanillaCartState.updateCart(current);
      else document.dispatchEvent(new CustomEvent('cart:updated', { detail: current }));
      if (window.location.pathname.replace(/\/$/, '') === `${root}cart`.replace(/\/$/, '') && document.documentElement.dataset.luffCheckingOut !== 'true') {
        window.location.reload();
      }
    }
    return current;
  }

  function reconcile() {
    if (running) {
      pending = true;
      return running;
    }
    running = (async () => {
      let result;
      do {
        pending = false;
        result = await reconcileOnce();
      } while (pending);
      return result;
    })().catch(error => {
      console.error('Cart offer reconciliation failed:', error);
      throw error;
    }).finally(() => { running = null; });
    return running;
  }

  async function addPaidExtra(key, amount) {
    const current = await cart();
    const line = current.items.find(item => item.key === key);
    if (!line || amount < 1) return current;
    await post('add', { id: line.variant_id, quantity: amount });
    const updated = await cart();
    if (window.VanillaCartState?.updateCart) window.VanillaCartState.updateCart(updated);
    return updated;
  }

  window.LuffCartOffers = { reconcile, addPaidExtra, propertyName };
  document.addEventListener('cart:updated', () => { reconcile().catch(() => {}); });
  document.addEventListener('DOMContentLoaded', () => { reconcile().catch(() => {}); });
})();
